import _ from 'lodash';
import React from 'react';
import { Button } from 'design-system/Button';
import { makeStyles } from 'design-system/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
// @ts-ignore node module does not ship with types
import { CSVLink } from 'react-csv';

import { Chip, IconButton } from 'design-system';
import { AuditResult, BenchmarkIIAuditResult, SpeedAuditVersion } from '@scale/shared/defaults';
import { IInstructionItemJson } from '@scale/shared/types/instructionitem';
import { IWorkerIdToEmailMap } from '@scale/shared/types/corp/quality';
import {
  BenchmarkThresholdAuditBatch,
  resultColors,
  SpeedAuditResult,
  SubtaskAssignment,
} from './types';
import LookupObjectLink from 'react-scale/corp/components/Lookup/LookupObjectLink';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';
import { logCorpEvent } from '@scale/frontend-lib/analytics/log-corp-event';
import { Event } from '@scale/shared/types/events';
import {
  ElementClickedType,
  GeneralElementClicked,
} from '@scale/shared/types/events/GeneralElementClicked';
import { speedAuditEventPrefix } from 'react-scale/components/audits/SpeedAudit/utils';

interface IInsructionItemResultProps {
  attemptAuditId: string;
  auditBatch: BenchmarkThresholdAuditBatch;
  instructionItemId: string;
  projectInstrItems: IInstructionItemJson[];
}

function InstructionItemResult({
  attemptAuditId,
  instructionItemId,
  auditBatch,
  projectInstrItems,
}: IInsructionItemResultProps) {
  const instructionItem = projectInstrItems.find(ii => ii._id === instructionItemId);
  if (!instructionItem) return null;
  const recentScore = auditBatch.attemptAudits.find(
    attemptAudit => attemptAudit._id === attemptAuditId,
  )?.metadata?.workerInstrItemThresholds?.[instructionItemId];
  return (
    <Chip
      size="small"
      label={[instructionItem.name, recentScore && `(${recentScore.toFixed(2)})`].join(' ')}
    />
  );
}

interface SpeedAuditResultsProps {
  auditBatch: BenchmarkThresholdAuditBatch;
  projectInstrItems: IInstructionItemJson[];
  results: SpeedAuditResult[];
  isBenchmarkIIAudit: boolean;
  matchingResponses: SubtaskAssignment[];
  workerIdToEmailMap?: IWorkerIdToEmailMap;
  setCurrentAudit: (result: SpeedAuditResult) => void;
  speedAuditVersion?: SpeedAuditVersion;
}

const SpeedAuditResults = (props: SpeedAuditResultsProps) => {
  const styles = useSpeedAuditResultStyles();
  const results = props.isBenchmarkIIAudit ? BenchmarkIIAuditResult : _.values(AuditResult);
  const byScore: Record<string, SpeedAuditResult[]> = _.groupBy(
    props.results,
    result => result.result ?? 'Skip',
  );
  const responseByAttempt = _.keyBy(props.matchingResponses, response => response.auditLookup.id);
  const date = moment().format('YYYY-MM-DD');
  const csvFileName = `speed-audit-${props.results.length}-results-${date}.csv`;
  const anyMedia = props.results.some(result => result.media);
  let csvHeaders = ['Score', 'ObjectType', 'ObjectId', 'Note'];
  if (anyMedia) {
    csvHeaders = csvHeaders.concat(['Media URL', 'Image']);
  }
  const csvRowData = React.useMemo<string[][]>(() => {
    return props.results.map(result => {
      let mainRowData = [
        result.result ?? 'S',
        result.objectType,
        result.objectId,
        result.comments ?? '',
      ];
      if (anyMedia) {
        mainRowData = mainRowData.concat([
          result.media ?? '',
          result.media ? `=IMAGE(""${result.media}"")` : '', // preview image in google sheets
        ]);
      }
      return mainRowData;
    });
  }, [anyMedia, props.results]);
  return (
    <div className={styles.results}>
      <div className={styles.export}>
        <Button fullWidth={false}>
          <CSVLink
            className={styles.exportBtn}
            filename={csvFileName}
            headers={csvHeaders}
            data={csvRowData}
          >
            <CloudDownloadIcon /> Export CSV
          </CSVLink>
        </Button>
      </div>
      <div className={styles.scoreLists}>
        {results.map(resultType => (
          <div key={resultType} className={styles.scoreList}>
            <div className={styles.scoreHeading} style={{ borderColor: resultColors[resultType] }}>
              {resultType}
              <IconButton
                size="small"
                data-tip="Copy to clipboard"
                onClick={() =>
                  copyToClipboard(byScore[resultType]?.map(result => result.objectId).join(', '), {
                    excludeOuterQuotes: true,
                  })
                }
              >
                <FontAwesomeIcon icon="copy" color="lightgrey" />
              </IconButton>
            </div>
            {(byScore[resultType] || []).map(result => {
              const response = responseByAttempt[result.objectId];
              // TODO: In SpeedAudit, retrieve & pass subtask.attempters before normalize is called on the lookupObject
              /*
                TypeScript complains here about retrieving .attempters, because
                normalizeAssignment should technically filter out .attempters
                after the attemptAudit lookup in SpeedAudit.tsx.
              */
              // @ts-ignore
              const reviewerID: string = _.last(response?.subtask?.attempters || []) || '';
              const reviewerIDOrEmail = props.workerIdToEmailMap?.[reviewerID]?.email || reviewerID;
              return (
                <div key={result.objectId} className={styles.resultLine}>
                  <div>
                    <span
                      onClick={() => props.setCurrentAudit(result)} // TODO: Scroll page to top on click
                      className={styles.objectType}
                    >
                      {result.objectType}
                    </span>
                    <span style={{ float: 'right' }}>
                      <LookupObjectLink
                        onClick={() =>
                          logCorpEvent(Event.GeneralElementClicked, {
                            name: `${speedAuditEventPrefix(
                              props.speedAuditVersion,
                            )} > Click subtask nav link`,
                            type: ElementClickedType.Link,
                          } as GeneralElementClicked)
                        }
                        shouldOpenNewTab
                        objectId={result.objectId}
                      />
                    </span>
                    <br />
                    {reviewerIDOrEmail ? (
                      <span style={{ float: 'right' }}>
                        <LookupObjectLink objectId={reviewerIDOrEmail} />
                      </span>
                    ) : (
                      'No Reviewer'
                    )}
                    <br />
                  </div>
                  {!!result.taskInstructionItems?.length && (
                    <div>
                      <FontAwesomeIcon style={{ opacity: 0.3 }} icon="tag" />
                      {` `}
                      {result.taskInstructionItems.map(ii => (
                        <InstructionItemResult
                          key={ii}
                          projectInstrItems={props.projectInstrItems}
                          instructionItemId={ii}
                          auditBatch={props.auditBatch}
                          attemptAuditId={result.attemptAuditId}
                        />
                      ))}
                    </div>
                  )}
                  <div
                    className={styles.note}
                    style={{ display: result.comments ? 'block' : 'hidden' }}
                  ></div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

function CloudDownloadIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M0 0h24v24H0z" fill="none" />
      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM17 13l-5 5-5-5h3V9h4v4h3z" />
    </svg>
  );
}

const useSpeedAuditResultStyles = makeStyles({
  results: {
    paddingLeft: 24,
    paddingBottom: 24,
  },
  export: {
    paddingBottom: '24px',
  },
  exportBtn: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  exportText: {
    display: 'inline-block',
    marginLeft: '8px',
    lineHeight: '24px',
    fontSize: '16px',
    textTransform: 'uppercase',
  },
  scoreLists: {
    display: 'flex',
    flexDirection: 'row',
  },
  scoreList: {
    marginRight: '64px',
  },
  objectType: {
    opacity: 0.7,
    marginRight: '4px',
    fontWeight: 'bold',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  scoreHeading: {
    padding: '4px 1px',
    marginRight: '16px',
    color: 'black',
    fontSize: '16px',
    display: 'block',
    marginBottom: '8px',
    borderBottom: '2px solid white',
  },
  resultLine: {
    marginBottom: '4px',
  },
  note: {
    paddingTop: '4px',
    paddingBottom: '8px',
  },
});

export default React.memo(SpeedAuditResults);
